@tailwind base;
@tailwind components;
@tailwind utilities;

/*
 * Freshhlock brand palette — per the brand specification reproduced in
 * "Freshhlock Business Plan v1.2" (§ Brand palette):
 *
 *   Arctic Deep Blue  #16324F  wordmark, platform headers, B2B trust elements
 *   Harvest Amber     #E08A2C  logo mark, CTAs, fresh-frozen seal, campaign tags
 *   Glacier Teal      #1F7A8C  category tagging, B2C app accents
 *   Ice White         #EAF4F6  backgrounds, cold-chain visual cue
 *   Warm Sand         #F6F1E7  B2C storefront warmth, content sections
 *   Charcoal          #2B2B2B  body copy, specification sheets
 *
 * Values are stored as bare `L C H` triplets so Tailwind can compose them
 * with an alpha channel: oklch(var(--primary-500) / <alpha-value>).
 *
 * Scales come in two kinds, and the distinction is what makes dark mode work:
 *
 *   Theme-aware  background · foreground · primary · accent · secondary
 *                Flip with the active theme. Use for anything sitting on the
 *                page canvas — surfaces, body copy, headings, tints, chips.
 *
 *   Fixed        brand · paper · ink · sand
 *                Identical in both themes. `brand` is the deep navy used for
 *                solid hero/CTA/footer surfaces, `paper` the near-white text
 *                and hairlines that sit on them, `ink` the always-dark chrome
 *                (mobile menu, tooltips), `sand` the warm content band.
 */

:root {
  /* Typography */
  --font-heading: 'Manrope', sans-serif;
  --font-body: 'Inter', sans-serif;
  --font-label: 'Inter', sans-serif;

  /* ── Theme-aware ─────────────────────────────────────────────────────── */

  /* Surfaces — Ice White #EAF4F6 at the 50 stop, darkening upward */
  --background-50: 0.960 0.011 211;
  --background-100: 0.938 0.011 211;
  --background-200: 0.902 0.011 211;
  --background-300: 0.862 0.010 211;
  --background-400: 0.815 0.010 211;
  --background-500: 0.755 0.010 211;
  --background-600: 0.672 0.009 211;
  --background-700: 0.565 0.009 211;
  --background-800: 0.450 0.008 211;
  --background-900: 0.320 0.007 211;
  --background-950: 0.200 0.005 211;

  /* Body copy — Charcoal #2B2B2B at the 600 stop, neutral by specification */
  --foreground-50: 0.980 0 0;
  --foreground-100: 0.930 0 0;
  --foreground-200: 0.850 0 0;
  --foreground-300: 0.730 0 0;
  /* 0.540 is the lightest neutral that still clears 4.5:1 on the Ice White
     canvas — this stop carries small caption text in ~34 places. */
  --foreground-400: 0.540 0 0;
  --foreground-500: 0.430 0 0;
  --foreground-600: 0.289 0 0;
  --foreground-700: 0.250 0 0;
  --foreground-800: 0.212 0 0;
  --foreground-900: 0.172 0 0;
  --foreground-950: 0.128 0 0;

  /* Arctic Deep Blue #16324F at the 500 stop */
  --primary-50: 0.970 0.008 251.2;
  --primary-100: 0.912 0.017 251.2;
  --primary-200: 0.822 0.030 251.2;
  --primary-300: 0.682 0.045 251.2;
  --primary-400: 0.492 0.058 251.2;
  --primary-500: 0.311 0.0625 251.2;
  --primary-600: 0.278 0.060 251.2;
  --primary-700: 0.240 0.055 251.2;
  --primary-800: 0.200 0.047 251.2;
  --primary-900: 0.160 0.038 251.2;
  --primary-950: 0.115 0.028 251.2;

  /* Harvest Amber #E08A2C at the 500 stop */
  --accent-50: 0.985 0.016 62.8;
  --accent-100: 0.955 0.040 62.8;
  --accent-200: 0.910 0.075 62.8;
  --accent-300: 0.850 0.108 62.8;
  --accent-400: 0.785 0.132 62.8;
  --accent-500: 0.709 0.146 62.8;
  --accent-600: 0.638 0.140 62.8;
  --accent-700: 0.545 0.118 62.8;
  --accent-800: 0.440 0.094 62.8;
  --accent-900: 0.335 0.068 62.8;
  --accent-950: 0.215 0.042 62.8;

  /* Glacier Teal #1F7A8C at the 500 stop */
  --secondary-50: 0.975 0.012 214.3;
  --secondary-100: 0.940 0.024 214.3;
  --secondary-200: 0.885 0.042 214.3;
  --secondary-300: 0.800 0.060 214.3;
  --secondary-400: 0.672 0.077 214.3;
  --secondary-500: 0.536 0.0854 214.3;
  --secondary-600: 0.478 0.083 214.3;
  --secondary-700: 0.410 0.073 214.3;
  --secondary-800: 0.335 0.060 214.3;
  --secondary-900: 0.255 0.046 214.3;
  --secondary-950: 0.170 0.030 214.3;

  /* ── Fixed (identical in both themes) ────────────────────────────────── */

  /* Solid brand surfaces — Arctic Deep Blue, never inverted */
  --brand-50: 0.970 0.008 251.2;
  --brand-100: 0.912 0.017 251.2;
  --brand-200: 0.822 0.030 251.2;
  --brand-300: 0.682 0.045 251.2;
  --brand-400: 0.492 0.058 251.2;
  --brand-500: 0.311 0.0625 251.2;
  --brand-600: 0.278 0.060 251.2;
  --brand-700: 0.240 0.055 251.2;
  --brand-800: 0.200 0.047 251.2;
  --brand-900: 0.160 0.038 251.2;
  --brand-950: 0.115 0.028 251.2;

  /* Text and hairlines that sit on brand/ink surfaces */
  --paper-50: 1 0 0;
  --paper-100: 0.968 0.005 211;
  --paper-200: 0.920 0.008 211;
  --paper-300: 0.845 0.010 211;
  --paper-400: 0.755 0.011 211;
  --paper-500: 0.660 0.011 211;

  /* Always-dark chrome — mobile menu, tooltips, overlays */
  --ink-50: 0.960 0.004 251;
  --ink-100: 0.900 0.006 251;
  --ink-200: 0.800 0.008 251;
  --ink-300: 0.660 0.010 251;
  --ink-400: 0.500 0.012 251;
  --ink-500: 0.395 0.014 251;
  --ink-600: 0.320 0.016 251;
  --ink-700: 0.262 0.018 251;
  --ink-800: 0.208 0.018 251;
  --ink-900: 0.160 0.018 251;
  --ink-950: 0.118 0.016 251;

  /* Warm Sand #F6F1E7 content band */
  --sand-500: 0.959 0.0143 84.6;

  /*
   * Elevated surface — cards and the sticky header. The sand band (0.959) and
   * --background-50 (0.960) are the same lightness, so a surface drawn in
   * either canvas colour reads as flat against its own section. Elevated
   * surfaces use pure white plus a border here, and step *above* the canvas
   * in dark theme.
   */
  --card-50: 1 0 0;
  --card-200: 0.920 0.008 211;
}

/*
 * Dark theme. The shell writes data-theme on <html> before first paint; the
 * media query is the fallback for the standalone Vite build, which has no
 * theme switcher. An explicit data-theme always wins over the system setting.
 */
:root[data-theme='dark'] {
  /* Surfaces invert — deep arctic navy canvas, lighter cards above it */
  --background-50: 0.168 0.017 251;
  --background-100: 0.212 0.018 251;
  --background-200: 0.278 0.018 251;
  --background-300: 0.340 0.017 251;
  --background-400: 0.412 0.016 251;
  --background-500: 0.500 0.014 251;
  --background-600: 0.595 0.012 251;
  --background-700: 0.690 0.010 251;
  --background-800: 0.788 0.008 251;
  --background-900: 0.882 0.006 251;
  --background-950: 0.958 0.004 251;

  /* Body copy inverts with it */
  --foreground-50: 0.145 0 0;
  --foreground-100: 0.198 0 0;
  --foreground-200: 0.290 0 0;
  --foreground-300: 0.430 0 0;
  --foreground-400: 0.600 0 0;
  --foreground-500: 0.735 0 0;
  --foreground-600: 0.872 0 0;
  --foreground-700: 0.905 0 0;
  --foreground-800: 0.932 0 0;
  --foreground-900: 0.955 0 0;
  --foreground-950: 0.978 0 0;

  /* Brand tints darken, brand text lightens — same hue throughout */
  --primary-50: 0.200 0.020 251.2;
  --primary-100: 0.248 0.028 251.2;
  --primary-200: 0.310 0.040 251.2;
  --primary-300: 0.420 0.052 251.2;
  --primary-400: 0.560 0.062 251.2;
  --primary-500: 0.680 0.070 251.2;
  --primary-600: 0.742 0.068 251.2;
  --primary-700: 0.800 0.058 251.2;
  --primary-800: 0.858 0.045 251.2;
  --primary-900: 0.908 0.032 251.2;
  --primary-950: 0.950 0.020 251.2;

  /* Amber holds its 500 stop so CTAs look identical in both themes */
  --accent-50: 0.215 0.030 62.8;
  --accent-100: 0.268 0.045 62.8;
  --accent-200: 0.340 0.065 62.8;
  --accent-300: 0.440 0.090 62.8;
  --accent-400: 0.580 0.120 62.8;
  --accent-500: 0.709 0.146 62.8;
  --accent-600: 0.775 0.135 62.8;
  --accent-700: 0.840 0.110 62.8;
  --accent-800: 0.892 0.082 62.8;
  --accent-900: 0.935 0.055 62.8;
  --accent-950: 0.970 0.030 62.8;

  --secondary-50: 0.205 0.022 214.3;
  --secondary-100: 0.258 0.032 214.3;
  --secondary-200: 0.330 0.045 214.3;
  --secondary-300: 0.430 0.060 214.3;
  --secondary-400: 0.560 0.075 214.3;
  --secondary-500: 0.672 0.082 214.3;
  --secondary-600: 0.745 0.076 214.3;
  --secondary-700: 0.812 0.064 214.3;
  --secondary-800: 0.870 0.050 214.3;
  --secondary-900: 0.918 0.034 214.3;
  --secondary-950: 0.958 0.020 214.3;

  /* Warm Sand becomes a deep warm band rather than a pale one */
  --sand-500: 0.205 0.010 84.6;

  /* Cards lift above the dark sand band rather than going white */
  --card-50: 0.212 0.018 251;
  --card-200: 0.278 0.018 251;
}

@media (prefers-color-scheme: dark) {
  :root:not([data-theme]) {
    --background-50: 0.168 0.017 251;
    --background-100: 0.212 0.018 251;
    --background-200: 0.278 0.018 251;
    --background-300: 0.340 0.017 251;
    --background-400: 0.412 0.016 251;
    --background-500: 0.500 0.014 251;
    --background-600: 0.595 0.012 251;
    --background-700: 0.690 0.010 251;
    --background-800: 0.788 0.008 251;
    --background-900: 0.882 0.006 251;
    --background-950: 0.958 0.004 251;

    --foreground-50: 0.145 0 0;
    --foreground-100: 0.198 0 0;
    --foreground-200: 0.290 0 0;
    --foreground-300: 0.430 0 0;
    --foreground-400: 0.600 0 0;
    --foreground-500: 0.735 0 0;
    --foreground-600: 0.872 0 0;
    --foreground-700: 0.905 0 0;
    --foreground-800: 0.932 0 0;
    --foreground-900: 0.955 0 0;
    --foreground-950: 0.978 0 0;

    --primary-50: 0.200 0.020 251.2;
    --primary-100: 0.248 0.028 251.2;
    --primary-200: 0.310 0.040 251.2;
    --primary-300: 0.420 0.052 251.2;
    --primary-400: 0.560 0.062 251.2;
    --primary-500: 0.680 0.070 251.2;
    --primary-600: 0.742 0.068 251.2;
    --primary-700: 0.800 0.058 251.2;
    --primary-800: 0.858 0.045 251.2;
    --primary-900: 0.908 0.032 251.2;
    --primary-950: 0.950 0.020 251.2;

    --accent-50: 0.215 0.030 62.8;
    --accent-100: 0.268 0.045 62.8;
    --accent-200: 0.340 0.065 62.8;
    --accent-300: 0.440 0.090 62.8;
    --accent-400: 0.580 0.120 62.8;
    --accent-500: 0.709 0.146 62.8;
    --accent-600: 0.775 0.135 62.8;
    --accent-700: 0.840 0.110 62.8;
    --accent-800: 0.892 0.082 62.8;
    --accent-900: 0.935 0.055 62.8;
    --accent-950: 0.970 0.030 62.8;

    --secondary-50: 0.205 0.022 214.3;
    --secondary-100: 0.258 0.032 214.3;
    --secondary-200: 0.330 0.045 214.3;
    --secondary-300: 0.430 0.060 214.3;
    --secondary-400: 0.560 0.075 214.3;
    --secondary-500: 0.672 0.082 214.3;
    --secondary-600: 0.745 0.076 214.3;
    --secondary-700: 0.812 0.064 214.3;
    --secondary-800: 0.870 0.050 214.3;
    --secondary-900: 0.918 0.034 214.3;
    --secondary-950: 0.958 0.020 214.3;

    --sand-500: 0.205 0.010 84.6;

    --card-50: 0.212 0.018 251;
    --card-200: 0.278 0.018 251;
  }
}

/* Warm Sand content band */
.warm-sand-bg {
  background-color: oklch(var(--sand-500));
}

@layer base {
  html {
    scroll-behavior: smooth;
  }

  body {
    font-family: var(--font-body);
    background-color: oklch(var(--background-50));
    color: oklch(var(--foreground-600));
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-heading);
  }
}

@layer components {
  .container-page {
    @apply w-full max-w-[1280px] mx-auto px-4 md:px-6;
  }
}

@layer utilities {
  .text-balance {
    text-wrap: balance;
  }
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeInRight {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes countUp {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.7s ease-out forwards;
}

.animate-fade-in-left {
  animation: fadeInLeft 0.7s ease-out forwards;
}

.animate-fade-in-right {
  animation: fadeInRight 0.7s ease-out forwards;
}

.animate-delay-100 { animation-delay: 0.1s; }
.animate-delay-200 { animation-delay: 0.2s; }
.animate-delay-300 { animation-delay: 0.3s; }
.animate-delay-400 { animation-delay: 0.4s; }
.animate-delay-500 { animation-delay: 0.5s; }

.animate-slide-up {
  animation: slideUp 0.3s ease-out forwards;
}

.scroll-reveal {
  opacity: 0;
}

.scroll-reveal.revealed {
  animation: fadeInUp 0.7s ease-out forwards;
}

/* Safe area padding for mobile bottom nav */
.safe-area-bottom {
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

/* Honeypot field hiding */
.field-website_alt,
.contact-honeypot {
  position: absolute;
  left: -9999px;
  opacity: 0;
  height: 0;
  width: 0;
  overflow: hidden;
  pointer-events: none;
}

/* Card scroll micro-animations */
.card-animate {
  transform: translateY(20px);
  transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

.card-animate.card-revealed {
  transform: translateY(0);
}

/* Stagger animation when parent section is revealed */
.scroll-reveal.revealed .card-animate {
  transform: translateY(0);
}

.scroll-reveal.revealed .card-animate:nth-child(1) { transition-delay: 0.06s; }
.scroll-reveal.revealed .card-animate:nth-child(2) { transition-delay: 0.12s; }
.scroll-reveal.revealed .card-animate:nth-child(3) { transition-delay: 0.18s; }
.scroll-reveal.revealed .card-animate:nth-child(4) { transition-delay: 0.24s; }
.scroll-reveal.revealed .card-animate:nth-child(5) { transition-delay: 0.30s; }
.scroll-reveal.revealed .card-animate:nth-child(6) { transition-delay: 0.36s; }
.scroll-reveal.revealed .card-animate:nth-child(7) { transition-delay: 0.42s; }
.scroll-reveal.revealed .card-animate:nth-child(8) { transition-delay: 0.48s; }

/* Icon animations */
@keyframes iconFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}

.animate-icon-float {
  animation: iconFloat 3s ease-in-out infinite;
}

/* Enhanced hover pop with bounce easing */
.icon-hover-pop {
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.group:hover .icon-hover-pop {
  transform: scale(1.14) rotate(5deg);
}

/* Frosted glass hover glow effect */
.frost-glow {
  position: relative;
  transition: box-shadow 0.4s ease, border-color 0.4s ease;
}

.frost-glow::before {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%, oklch(var(--secondary-500) / 0.15) 100%);
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  opacity: 0;
  transition: opacity 0.4s ease;
  pointer-events: none;
}

.frost-glow:hover::before {
  opacity: 1;
}

.frost-glow:hover {
  box-shadow: 0 0 30px oklch(var(--secondary-500) / 0.08), 0 0 60px rgba(255, 255, 255, 0.03);
  border-color: oklch(var(--secondary-400) / 0.4);
}

/* Ice Crystal Rain animations */
@keyframes crystalFall {
  0% {
    transform: translateY(0) translateX(0) var(--crystal-rotation-start, rotate(0deg));
    opacity: var(--start-opacity, 0.6);
  }
  15% {
    opacity: var(--peak-opacity, 0.8);
  }
  85% {
    opacity: var(--end-opacity, 0.2);
  }
  100% {
    transform: translateY(calc(100vh + 40px)) translateX(var(--crystal-drift, 0px)) rotate(var(--crystal-rotation-end, 360deg));
    opacity: 0;
  }
}

@keyframes crystalRotate {
  0% {
    transform: rotate(var(--crystal-rotation-start, 0deg));
  }
  100% {
    transform: rotate(var(--crystal-rotation-end, 360deg));
  }
}

.hex-crystal {
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
}

.star-crystal {
  clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
}